import styles from './ProjectsPage.module.scss';
import React from 'react';
import { useEffect, useState } from 'react';
import { BulkBar } from '../../components/projects/BulkBar/BulkBar';
import { DeleteProjectModal } from '../../components/projects/DeleteProjectModal/DeleteProjectModal';
import { NewProjectModal, NewProjectValues } from '../../components/projects/NewProjectModal/NewProjectModal';
import { PageHeader } from '../../components/projects/PageHeader/PageHeader';
import { ProjectsTable } from '../../components/projects/ProjectsTable/ProjectsTable';
import { ProjectsToolbar } from '../../components/projects/ProjectsToolbar/ProjectsToolbar';
import { StopListModal } from '../../components/projects/StopListModal/StopListModal';
import { TableFooter } from '../../components/ui/TableFooter/TableFooter';
import { useToast } from '../../components/ui/Toast/ToastProvider';
import { useProjectsTable } from '../../hooks/useProjectsTable';
import type { Project } from '../../types/projectsTable';
import { useLocation, useNavigate } from 'react-router-dom';
import { useGetOrgBlockedHostsQuery } from '../../entities/blocks/api/blocksApi';
import { useAppSelector } from '../../app/hooks';
import { selectOrganizationId } from '../../entities/organization/model/slice';

type DeleteTarget = Project | number | null;

export function ProjectsPage() {
    const notify = useToast();
    const table = useProjectsTable();

    const organizationId = useAppSelector(selectOrganizationId);

    const { data: blockedHosts } = useGetOrgBlockedHostsQuery(organizationId ?? '', {
        skip: !organizationId,
    });

    const [isStopListOpen, setStopListOpen] = useState(false);
    const [isNewProjectOpen, setNewProjectOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState<DeleteTarget>(null);

    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        if ((location.state as { openNew?: boolean } | null)?.openNew) {
            setNewProjectOpen(true);
            navigate(location.pathname, { replace: true, state: null });
        }
    }, [location.state, location.pathname, navigate]);

    const [createError, setCreateError] = useState<string | null>(null);
    const [creating, setCreating] = useState(false);

    const handleCreate = async (values: NewProjectValues) => {
        setCreating(true);
        setCreateError(null);

        try {
            const project = await table.addProject(values);
            setNewProjectOpen(false);
            navigate(`/projects/${project.id}`);
        } catch (error) {
            const err = error as { status?: number; data?: any };

            const symbolsLimit =
                err.data?.conflict_message?.exceeded_products?.[0]?.symbols_limit;

            if (symbolsLimit) {
                setCreateError(
                    `Введено более ${symbolsLimit} символов. Сократите наименование товара и загрузите файл повторно`,
                );
            } else if (err.status === 409) {
                setCreateError('Такое наименование проекта уже используется');
            } else {
                setCreateError('Не удалось создать проект. Попробуйте позже');
            }
        } finally {
            setCreating(false);
        }
    };

    const handleConfirmDelete = async () => {
        if (typeof deleteTarget === 'number') {
            await table.removeProjects(Array.from(table.selected));
            notify(`Удалено проектов: ${deleteTarget}`);
        } else if (deleteTarget) {
            await table.removeProjects([deleteTarget.id]);
            notify(`Проект «${deleteTarget.name}» удалён`);
        }
        setDeleteTarget(null);
    };

    return (
        <main className={styles.content}>
            <PageHeader
                stopListCount={blockedHosts?.length ?? 0}
                onOpenStopList={() => setStopListOpen(true)}
                onCreateProject={() => setNewProjectOpen(true)}
            />

            <ProjectsToolbar
                query={table.query}
                status={table.status}
                period={table.period}
                author={table.author}
                authors={table.authors}
                counts={table.counts}
                isFiltered={table.isFiltered}
                onQueryChange={table.setQuery}
                onStatusChange={table.setStatus}
                onPeriodChange={table.setPeriod}
                onAuthorChange={table.setAuthor}
                onReset={() => {
                    table.resetFilters();
                    notify('Фильтры сброшены');
                }}
            />

            <ProjectsTable
                scrollKey={`${table.page}-${table.sort.key}-${table.sort.dir}`}
                rows={table.pageRows}
                selected={table.selected}
                sort={table.sort}
                onToggleRow={table.toggleRow}
                onTogglePage={table.togglePage}
                onSort={table.toggleSort}
                onDelete={(project) => setDeleteTarget(project)}
                bulkBar={
                    <BulkBar
                        count={table.selected.size}
                        onExport={() => notify(`Выгрузка проектов: ${table.selected.size}`)}
                        onDelete={() => setDeleteTarget(table.selected.size)}
                        onClear={table.clearSelection}
                    />
                }
                footer={
                    <TableFooter
                        page={table.page}
                        totalPages={table.totalPages}
                        rangeLabel={table.rangeLabel}
                        onPageChange={table.setPage}
                    />
                }
            />

            <NewProjectModal
                open={isNewProjectOpen}
                submitting={creating}
                serverError={createError}
                onClose={() => {
                    setNewProjectOpen(false);
                    setCreateError(null);
                }}
                onCreate={handleCreate}
            />

            <StopListModal
                open={isStopListOpen}
                onClose={() => setStopListOpen(false)}
                onNotify={notify}
                organizationId={organizationId ?? undefined}
            />

            <DeleteProjectModal
                open={deleteTarget !== null}
                target={deleteTarget}
                onClose={() => setDeleteTarget(null)}
                onConfirm={handleConfirmDelete}
            />
        </main>
    );
}